











import { Component, Vue, Prop } from 'vue-property-decorator'
import { ExStore } from 'vuex'
import { EventEmitter } from 'events'
import { LoginUser } from '@/types/user'
import WebSocketProvider from '@/websocket'
import { WebSocketClientInterface } from '@/websocket/types'
import * as receiveMessages from '@/websocket/receivers/call/receiveMessages'
import { WebSocketMessageMode } from '@/websocket/constants'
import {
  WebSocketCallMessageTypes,
  VideoCallDeniedReasonCode
} from '@/websocket/receivers/call'
import WebRTCProvider, { WebRTC } from '@/webrtc'
import { isCordovaEnvironment, isPlatformIOS } from '@/utils/cordova'

const enum ResponseTypes {
  ACCEPT,
  DENY
}

const enum VideoCallEventTypes {
  REMOTE_REQUEST_ACCEPTED,
  REMOTE_REQUEST_DENIED
}

@Component({
  components: {
    VideoCallIncoming: () =>
      import('@/components/common/videoCallRoom/VideoCallIncoming.vue')
  }
})
export default class VideoCallIncomingContainer extends Vue {
  @Prop(Object) user!: LoginUser

  $store!: ExStore
  events = new EventEmitter()
  webSocket!: WebSocketClientInterface
  webrtc: WebRTC | null = null

  get inCallWebView() {
    return this.$store.getters['videoCallRoom/inCallWebView']
  }

  get isCalling() {
    return this.$store.getters['videoCallRoom/isCalling']
  }

  get isCaller() {
    return this.$store.getters['videoCallRoom/isCaller']
  }

  get roomId() {
    return this.$store.getters['videoCallRoom/roomId']
  }

  get pinCode() {
    return this.$store.getters['videoCallRoom/pinCode']
  }

  get roomSecret() {
    return this.$store.getters['videoCallRoom/roomSecret']
  }

  get requestPendingRoomId() {
    return this.$store.getters['videoCallRoom/requestPendingRoomId']
  }

  get caller() {
    return this.$store.getters['videoCallRoom/caller']
  }

  get callerOccupation() {
    return this.$store.getters['videoCallRoom/callerOccupation']
  }

  get videoRoomReady() {
    return this.$store.getters['videoCallRoom/videoRoomReady']
  }

  async created() {
    this.webSocket = WebSocketProvider.createConnection()
    this.webSocket.subscribeMessage(
      WebSocketMessageMode.CALL,
      WebSocketCallMessageTypes.REQUEST,
      this.handleReceiveCallRequest.bind(this)
    )
    this.webSocket.subscribeMessage(
      WebSocketMessageMode.CALL,
      WebSocketCallMessageTypes.CANCELED,
      this.handleRemoteCanceled.bind(this)
    )
    this.$postMessage.addEventListener('activateApp', () => {
      this.webSocket = WebSocketProvider.createConnection()
    })
  }

  /**
   * 切断
   */
  handleRemoteCanceled(
    message: receiveMessages.ReceiveMessageBodyCallCanceled
  ) {
    const { from } = message

    if (from === this.user.id) {
      return
    }

    if (!this.requestPendingRoomId) {
      return
    }

    if (!this.caller || this.caller.id !== from) {
      return
    }

    this.$store.commit('videoCallRoom/setRequestPendingRoomId', null)
    this.$store.dispatch('call/updateMissedHistory')

    if (this.inCallWebView) {
      this.$router.replace({ name: 'Hangup' })
    }
  }

  /**
   * 着信処理
   */
  async handleReceiveCallRequest(
    message: receiveMessages.ReceiveMessageBodyCallRequest
  ) {
    const { roomId, secret, pin, from, fromOccupation } = message

    if (this.user.id === from.id) {
      return
    }
    //通話中・着信中の同じroomへの着信は無視する
    if (
      (this.isCalling && this.roomId == roomId) ||
      this.requestPendingRoomId == roomId
    ) {
      return
    }
    // 通話中または着信中の場合、自動で着信拒否する
    if (this.isCalling || this.requestPendingRoomId) {
      this.webSocket.send(WebSocketMessageMode.CALL, {
        type: WebSocketCallMessageTypes.DENIED,
        reasonCode: VideoCallDeniedReasonCode.DENIED,
        to: from.id,
        roomId
      })
      return
    }

    this.$store.commit('videoCallRoom/setCaller', from)
    this.$store.commit('videoCallRoom/setCallerOccupation', fromOccupation)
    this.$store.commit('videoCallRoom/setRequestPendingRoomId', roomId)
    this.events.removeAllListeners()

    const result = await new Promise<{
      type: ResponseTypes
      [key: string]: any
    }>(resolve => {
      this.events.once(
        VideoCallEventTypes.REMOTE_REQUEST_ACCEPTED,
        videoEnable => resolve({ type: ResponseTypes.ACCEPT, videoEnable })
      )
      this.events.once(VideoCallEventTypes.REMOTE_REQUEST_DENIED, () =>
        resolve({ type: ResponseTypes.DENY })
      )
    })

    this.$store.commit('videoCallRoom/setRequestPendingRoomId', null)

    const params = {
      videoEnable: result.videoEnable,
      roomId,
      secret,
      pin,
      isPatient: this.user.isPatient,
      isRelative: this.user.isPatientRelative,
      fromUser: from,
      fromOccupation: fromOccupation
    }

    switch (result.type) {
      case ResponseTypes.ACCEPT:
        if (isCordovaEnvironment() && !this.inCallWebView) {
          if (isPlatformIOS()) {
            if (this.user.isPatient || this.user.isPatientRelative) {
              this.$router.replace({ name: 'PatientHangupInner' })
            } else {
              this.$router.replace({ name: 'WorkerHangupInner' })
            }
          }
          this.$postMessage.showCallReceiveView(params)
        } else {
          await this.$store.dispatch('videoCallRoom/setAsCallee', {
            roomId,
            roomSecret: secret,
            pinCode: pin
          })
          this.webSocket.send(WebSocketMessageMode.CALL, {
            type: WebSocketCallMessageTypes.ACCEPTED,
            roomId
          })
          await WebRTCProvider.createConnection(
            this.user.videoApiSecret,
            this.roomId,
            this.pinCode,
            this.roomSecret,
            result.videoEnable
          )
          this.$store.commit('videoCallRoom/switchCalling', true)
        }
        break
      default:
        this.webSocket.send(WebSocketMessageMode.CALL, {
          type: WebSocketCallMessageTypes.DENIED,
          reasonCode: VideoCallDeniedReasonCode.DENIED,
          to: from.id,
          roomId
        })
        this.$store.commit('videoCallRoom/clearRoom')
    }
  }

  handleAccept(videoEnable: boolean) {
    this.$store.commit('videoCallRoom/videoActive', videoEnable)

    this.$emit('accepted')

    const notifier = () =>
      this.events.emit(VideoCallEventTypes.REMOTE_REQUEST_ACCEPTED, videoEnable)

    if (this.videoRoomReady) {
      notifier()
    } else {
      const unsubscribe = this.$store.subscribe(({ type, payload }) => {
        if (type === 'videoCallRoom/setVideoRoomReady' && payload) {
          notifier()
          unsubscribe()
        }
      })
    }
  }

  handleDeny() {
    this.events.emit(VideoCallEventTypes.REMOTE_REQUEST_DENIED)
  }
}
